// This file contains the Sidebar component which is responsible for rendering the sidebar of the application. The sidebar displays the list of folders and prompts, and allows the user to interact with them.
import React, { useState, useCallback } from 'react';
import {
  Pencil,
  Trash2,
  PanelLeftClose,
  PanelLeftOpen,
  LayoutList,
  Inbox,
  Briefcase,
  Folder as FolderIcon,
} from 'lucide-react';
import { Folder, Prompt } from '../types';
import { useHybridTheme } from '../themes/useHybridTheme';
import { useThemeColors } from '../hooks/useThemeColors';
import { useFolderColor } from '../hooks/useFolderColor';
import { useFeaturesContext } from '../contexts/FeaturesContext';
import { useSettings } from '../contexts/SettingsContext';
import { Z_LAYERS } from '../constants/zLayers';

import { getIconComponent } from '../utils/icons';

interface SidebarProps {
  folders: Folder[];
  prompts: Prompt[];
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string) => void;
  onAddFolder: () => void;
  isCollapsed: boolean;
  isFullscreen?: boolean;
  onToggleCollapse: () => void;
  onRenameFolder?: (folderId: string) => void;
  onDeleteFolder?: (folderId: string) => void;
  workFilter: boolean;
  onWorkFilterChange: (enabled: boolean) => void;
}

export default function Sidebar({
  folders,
  prompts,
  selectedFolderId,
  onSelectFolder,
  onAddFolder: _onAddFolder,
  isCollapsed,
  isFullscreen = false,
  onToggleCollapse,
  onRenameFolder,
  onDeleteFolder,
  workFilter,
  onWorkFilterChange,
}: SidebarProps) {
  const colors = useThemeColors();
  const { enabledFeatures } = useFeaturesContext();
  const getFolderColor = useFolderColor();
  const { currentTheme } = useSettings();

  // Declarative tooltip for collapsed folders. Rendering it in React (instead
  // of imperatively appending divs to document.body and removing them on a
  // 150ms timer) means it can't leak orphan nodes on unmount / collapse / rapid
  // hover — React tears it down when hoveredFolder resets or the component
  // unmounts (#8).
  const [hoveredFolder, setHoveredFolder] = useState<{
    name: string;
    top: number;
    left: number;
  } | null>(null);

  // Runtime-interpolated Tailwind classes like `hover:bg-[${color}]` generate
  // no CSS (the JIT scanner can't see runtime values), so hover feedback was
  // dead (#107). Drive it with inline style via mouse handlers instead. On
  // leave we restore the element's base background (its selected/active color
  // or transparent) rather than assuming transparent.
  const hoverBg = useCallback(
    (baseBackground: string) => ({
      onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.style.backgroundColor = colors.sidebar.hoverBackground;
      },
      onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.style.backgroundColor = baseBackground;
      },
    }),
    [colors.sidebar.hoverBackground]
  );

  // Determine which header image to use based on the theme and sidebar state
  const headerImage = isCollapsed
    ? currentTheme === 'light'
      ? 'icons/TopPromptIcon.png'
      : 'icons/TopPromptIcon.png'
    : currentTheme === 'light'
      ? 'icons/TopPromptLibraryHeader.png'
      : 'icons/TopPromptLibraryHeaderDark.png';

  // Calculate prompt counts
  const promptCounts: Record<string, number> = {
    all: prompts.length,
    unsorted: prompts.filter(p => !p.folder).length,
    ...folders.reduce(
      (acc, folder) => ({
        ...acc,
        [folder.id]: prompts.filter(p => p.folder === folder.id).length,
      }),
      {}
    ),
  };

  return (
    <div
      className={useHybridTheme().getClasses(
        `${isCollapsed ? 'w-[74px]' : 'w-[266px]'} relative ${isFullscreen ? 'h-full' : 'h-[600px]'} transition-all duration-300 overflow-hidden`
      )}
      style={{
        backgroundColor: colors.app.background,
      }}
    >
      <div
        style={{
          backgroundColor: colors.sidebar.background,
          borderColor: colors.sidebar.border,
        }}
        className={useHybridTheme().getClasses(
          'absolute left-[10px] right-0 top-[10px] bottom-[10px] border-r rounded-2xl overflow-hidden flex flex-col'
        )}
      >
        <div
          style={{ borderColor: colors.sidebar.border }}
          className={useHybridTheme().getClasses('flex items-center justify-center p-4 border-b')}
        >
          <a
            href="https://toppromptlibrary.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: colors.header.appName,
            }}
            className={useHybridTheme().getClasses(
              'flex items-center justify-center font-semibold'
            )}
            onMouseEnter={e => {
              e.currentTarget.style.color = colors.header.appNameHover;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = colors.header.appName;
            }}
          >
            <img
              src={headerImage}
              alt="Top Prompt Library"
              className={isCollapsed ? 'w-6 h-6' : 'h-6'}
              style={isCollapsed ? {} : { maxWidth: '160px' }}
            />
          </a>
        </div>

        <button
          onClick={() => onSelectFolder('all')}
          style={{
            backgroundColor:
              selectedFolderId === 'all' ? colors.sidebar.activeBackground : 'transparent',
            color: selectedFolderId === 'all' ? colors.sidebar.textSelected : colors.sidebar.text,
            borderColor: colors.sidebar.border,
            boxShadow:
              selectedFolderId === 'all' ? `0 0 0 2px ${colors.sidebar.textSelected}40` : 'none',
          }}
          className={`w-full p-4 border-b flex items-center ${
            isCollapsed ? 'justify-center' : 'space-x-2'
          } transition-all duration-150`}
          {...hoverBg(selectedFolderId === 'all' ? colors.sidebar.activeBackground : 'transparent')}
        >
          <LayoutList className="w-5 h-5" style={{ color: 'currentColor' }} />
          {!isCollapsed && (
            <div className="flex-1 flex justify-between items-center">
              <span className="font-medium">All Prompts</span>
              <span className="text-sm" style={{ color: colors.sidebar.textSecondary }}>
                {promptCounts.all}
              </span>
            </div>
          )}
        </button>

        <button
          onClick={() => onSelectFolder('unsorted')}
          style={{
            backgroundColor:
              selectedFolderId === 'unsorted' ? colors.sidebar.activeBackground : 'transparent',
            color:
              selectedFolderId === 'unsorted' ? colors.sidebar.textSelected : colors.sidebar.text,
            borderColor: colors.sidebar.border,
            boxShadow:
              selectedFolderId === 'unsorted'
                ? `0 0 0 2px ${colors.sidebar.textSelected}40`
                : 'none',
          }}
          className={`w-full p-4 border-b flex items-center ${
            isCollapsed ? 'justify-center' : 'space-x-2'
          } transition-all duration-150`}
          {...hoverBg(
            selectedFolderId === 'unsorted' ? colors.sidebar.activeBackground : 'transparent'
          )}
        >
          <Inbox className="w-5 h-5" style={{ color: 'currentColor' }} />
          {!isCollapsed && (
            <div className="flex-1 flex justify-between items-center">
              <span className="font-medium">Unsorted Prompts</span>
              <span className="text-sm" style={{ color: colors.sidebar.textSecondary }}>
                {promptCounts.unsorted}
              </span>
            </div>
          )}
        </button>

        <div className="flex-1 overflow-y-auto p-2 scrollbar-custom">
          {folders.map(folder => (
            <div
              key={folder.id}
              style={{
                backgroundColor:
                  selectedFolderId === folder.id ? colors.sidebar.activeBackground : 'transparent',
                color:
                  selectedFolderId === folder.id
                    ? colors.sidebar.textSelected
                    : colors.sidebar.text,
                boxShadow:
                  selectedFolderId === folder.id
                    ? `0 0 0 2px ${colors.sidebar.textSelected}40`
                    : 'none',
              }}
              className={`group w-full text-left px-3 py-2 rounded-lg mb-1 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} cursor-pointer transition-all duration-150`}
              onClick={() => onSelectFolder(folder.id)}
              onMouseEnter={e => {
                const baseBg =
                  selectedFolderId === folder.id ? colors.sidebar.activeBackground : 'transparent';
                e.currentTarget.style.backgroundColor = colors.sidebar.hoverBackground;
                if (isCollapsed) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setHoveredFolder({
                    name: folder.name,
                    left: rect.right + 8,
                    top: rect.top + rect.height / 2 - 12,
                  });
                }
                // Keep a reference so onMouseLeave restores the right base.
                e.currentTarget.dataset.baseBg = baseBg;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor =
                  e.currentTarget.dataset.baseBg ?? 'transparent';
                if (isCollapsed) {
                  setHoveredFolder(null);
                }
              }}
            >
              <div
                className={`flex items-center ${isCollapsed ? '' : 'space-x-2 flex-1'} cursor-pointer`}
              >
                {React.createElement(
                  enabledFeatures.folderIcons
                    ? getIconComponent(folder.icon || 'Folder')
                    : FolderIcon,
                  {
                    className: 'w-4 h-4',
                    style: {
                      color: enabledFeatures.folderColors ? getFolderColor(folder) : 'currentColor',
                      // Ensure Material Design icons are properly sized
                      fontSize: folder.icon?.startsWith('Md') ? '16px' : undefined,
                    },
                  }
                )}
                {!isCollapsed && (
                  <div className="flex-1 flex justify-between items-center">
                    <span className="truncate">{folder.name}</span>
                    <span className="text-sm" style={{ color: colors.sidebar.textSecondary }}>
                      {promptCounts[folder.id]}
                    </span>
                  </div>
                )}
              </div>
              {!isCollapsed && (
                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onRenameFolder?.(folder.id);
                    }}
                    style={{
                      backgroundColor: 'transparent',
                    }}
                    className="p-1 rounded-full"
                    {...hoverBg('transparent')}
                    title="Rename Folder"
                  >
                    <Pencil className="w-3.5 h-3.5" style={{ color: colors.sidebar.icon }} />
                  </button>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onDeleteFolder?.(folder.id);
                    }}
                    style={{
                      backgroundColor: 'transparent',
                    }}
                    className="p-1 rounded-full"
                    {...hoverBg('transparent')}
                    title="Delete Folder"
                  >
                    <Trash2 className="w-3.5 h-3.5" style={{ color: colors.sidebar.icon }} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div
          style={{ borderColor: colors.sidebar.border }}
          className={`${isCollapsed ? 'p-2' : 'p-4'} border-t space-y-2`}
        >
          {enabledFeatures.workPrompts && (
            <button
              onClick={() => onWorkFilterChange(!workFilter)}
              style={{
                color: workFilter ? colors.sidebar.textSelected : colors.sidebar.text,
                backgroundColor: workFilter ? colors.sidebar.activeBackground : 'transparent',
              }}
              className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-2 rounded-xl`}
              {...hoverBg(workFilter ? colors.sidebar.activeBackground : 'transparent')}
              title={isCollapsed ? 'Filter Work Prompts' : undefined}
            >
              <div className="flex items-center space-x-2">
                <Briefcase className="w-4 h-4" style={{ color: colors.sidebar.icon }} />
                {!isCollapsed && <span>Work Prompts</span>}
              </div>
            </button>
          )}

          <button
            onClick={onToggleCollapse}
            style={{ color: colors.sidebar.text }}
            className="w-full flex items-center px-4 py-2 rounded-xl"
            {...hoverBg('transparent')}
          >
            <div className="flex items-center space-x-2">
              {isCollapsed ? (
                <PanelLeftOpen
                  className="w-4 h-4"
                  style={{ color: colors.sidebar.collapseButton }}
                />
              ) : (
                <PanelLeftClose
                  className="w-4 h-4"
                  style={{ color: colors.sidebar.collapseButton }}
                />
              )}
              {!isCollapsed && (
                <span
                  style={{ color: colors.sidebar.collapseLabel }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = colors.sidebar.collapseLabelHover;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = colors.sidebar.collapseLabel;
                  }}
                >
                  Collapse
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Collapsed-folder tooltip, rendered declaratively so React owns its
          lifecycle (no orphaned document.body nodes — see #8). */}
      {hoveredFolder && (
        <div
          className="fixed px-2 py-1 text-sm rounded shadow-lg pointer-events-none transition-opacity duration-150"
          style={{
            color: colors.sidebar.textSelected,
            backgroundColor: colors.sidebar.activeBackground,
            left: hoveredFolder.left,
            top: hoveredFolder.top,
            zIndex: Z_LAYERS.TOOLTIP,
          }}
        >
          {hoveredFolder.name}
        </div>
      )}
    </div>
  );
}
